export const CART_CREATE = `mutation ProductShippingCart($input: CartInput!) {
  cartCreate(input: $input) {
    cart { id totalQuantity lines(first: 2) { nodes { quantity merchandise { ... on ProductVariant { id } } } } }
    userErrors { field message code }
  }
}`;

export const CART_RATES = `query ProductShippingRates($id: ID!) {
  cart(id: $id) { ...ProductShippingCarrierRates @defer }
}
fragment ProductShippingCarrierRates on Cart {
  deliveryGroups(first: 5, withCarrierRates: true) {
    nodes { deliveryOptions { code title deliveryMethodType estimatedCost { amount currencyCode } } }
    pageInfo { hasNextPage }
  }
}`;

export class ShippingError extends Error {
  constructor(code) { super(code); this.code = code; }
}

export function normalizePostalCode(value) {
  const cep = String(value).trim().replace(/^(\d{5})-(\d{3})$/, '$1$2');
  if (!/^\d{8}$/.test(cep) || /^0{8}$/.test(cep)) throw new ShippingError('invalid_cep');
  return cep;
}

export function decodeResponse(text, contentType) {
  if (!contentType.toLowerCase().includes('multipart/mixed')) return [JSON.parse(text)];
  const boundary = contentType.match(/boundary\s*=\s*(?:"([^"]+)"|([^;\s]+))/i);
  if (!boundary) throw new ShippingError('unavailable');
  const parts = text.split(`--${boundary[1] || boundary[2]}`).slice(1, -1).map((part) => {
    const match = part.match(/\r?\n\r?\n([\s\S]*)$/);
    if (!match) throw new ShippingError('unavailable');
    return JSON.parse(match[1].trim());
  });
  if (!parts.length || parts.at(-1).hasNext !== false) throw new ShippingError('unavailable');
  return parts;
}

export function ratesFromResponses(parts) {
  let groups;
  for (const part of parts) {
    if (part.errors?.length) throw new ShippingError('unavailable');
    if (part.data?.cart?.deliveryGroups) groups = part.data.cart.deliveryGroups;
    for (const patch of part.incremental || []) {
      if (patch.errors?.length) throw new ShippingError('unavailable');
      if (patch.path?.length === 1 && patch.path[0] === 'cart' && patch.data?.deliveryGroups) {
        groups = patch.data.deliveryGroups;
      }
    }
  }
  if (!groups || groups.pageInfo?.hasNextPage) throw new ShippingError('unavailable');
  if (!groups.nodes?.length) return [];
  if (groups.nodes.length !== 1) throw new ShippingError('split_delivery');
  const rates = (groups.nodes[0].deliveryOptions || []).filter((option) => option.deliveryMethodType === 'SHIPPING').map((option) => {
    const cost = option.estimatedCost;
    if (cost?.currencyCode !== 'BRL' || !/^\d+(?:\.\d{1,2})?$/.test(String(cost.amount))) {
      throw new ShippingError('unavailable');
    }
    const cents = Math.round(Number(cost.amount) * 100);
    if (!Number.isSafeInteger(cents)) throw new ShippingError('unavailable');
    return { name: String(option.title || option.code || 'Frete'), priceCents: cents };
  });
  return rates.sort((a, b) => a.priceCents - b.priceCents);
}

export async function quoteProduct({ variantId, quantity, postalCode, endpoint, signal, fetchImpl = fetch }) {
  const cep = normalizePostalCode(postalCode);
  if (!/^\d+$/.test(String(variantId))) throw new ShippingError('select_variant');
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 30) throw new ShippingError('invalid_quantity');
  const addressResponse = await fetchImpl(`https://viacep.com.br/ws/${cep}/json/`, { signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
  if (!addressResponse.ok) throw new ShippingError('unavailable');
  const address = await addressResponse.json();
  if (address.erro || !/^[A-Z]{2}$/.test(address.uf) || !address.localidade) throw new ShippingError('invalid_cep');
  const request = async (query, variables) => {
    const response = await fetchImpl(endpoint, {
      method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', signal,
      headers: { 'Content-Type': 'application/json', Accept: 'multipart/mixed, application/json' },
      body: JSON.stringify({ query, variables }),
    });
    if (!response.ok) throw new ShippingError('unavailable');
    return decodeResponse(await response.text(), response.headers.get('content-type') || 'application/json');
  };
  const merchandiseId = `gid://shopify/ProductVariant/${variantId}`;
  const [created] = await request(CART_CREATE, { input: {
    buyerIdentity: { countryCode: 'BR' },
    lines: [{ merchandiseId, quantity }],
    delivery: { addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: {
      countryCode: 'BR', provinceCode: address.uf, city: address.localidade, zip: cep,
    } } }] },
  } });
  const payload = created.data?.cartCreate;
  const cart = payload?.cart;
  if (created.errors?.length || payload?.userErrors?.length || !cart?.id) throw new ShippingError('unavailable');
  if (cart.totalQuantity !== quantity || cart.lines?.nodes?.length !== 1
    || cart.lines.nodes[0].quantity !== quantity || cart.lines.nodes[0].merchandise?.id !== merchandiseId) {
    throw new ShippingError('stock_changed');
  }
  return ratesFromResponses(await request(CART_RATES, { id: cart.id }));
}

export function deliveryLabel(minimum, maximum) {
  return minimum === maximum
    ? `${minimum} ${minimum === 1 ? 'dia útil' : 'dias úteis'}`
    : `${minimum}–${maximum} dias úteis`;
}

export async function quoteProductWithDelivery({ handle, variantId, quantity, postalCode, endpoint, signal, fetchImpl = fetch }) {
  const cep = normalizePostalCode(postalCode);
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(handle)) || String(handle).length > 200
    || !/^[1-9]\d{0,19}$/.test(String(variantId))) throw new ShippingError('select_variant');
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 30) throw new ShippingError('invalid_quantity');
  const response = await fetchImpl(endpoint, {
    method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', signal,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ handle, variantId: String(variantId), quantity, postalCode: cep }),
  });
  if (!response.ok) {
    if (response.status === 429) throw new ShippingError('rate_limited');
    const error = await response.json().catch(() => ({}));
    const cepErrors = ['invalid_cep', 'cep_not_found', 'cep_unavailable'];
    throw new ShippingError(cepErrors.includes(error.code) ? error.code : 'unavailable');
  }
  const result = await response.json();
  if (result.currency !== 'BRL' || !Array.isArray(result.rates) || result.rates.length > 20) throw new ShippingError('unavailable');
  const codes = new Set();
  return result.rates.map((rate) => {
    if (typeof rate.name !== 'string' || !rate.name || rate.name.length > 120
      || !/^[A-Z0-9_]{1,64}$/.test(rate.serviceCode) || codes.has(rate.serviceCode)
      || !Number.isSafeInteger(rate.priceCents) || rate.priceCents < 0
      || !Number.isInteger(rate.deliveryDaysMin) || rate.deliveryDaysMin < 1
      || !Number.isInteger(rate.deliveryDaysMax) || rate.deliveryDaysMax < rate.deliveryDaysMin || rate.deliveryDaysMax > 100) {
      throw new ShippingError('unavailable');
    }
    codes.add(rate.serviceCode);
    return { name: rate.name, serviceCode: rate.serviceCode, priceCents: rate.priceCents,
      deliveryDaysMin: rate.deliveryDaysMin, deliveryDaysMax: rate.deliveryDaysMax };
  }).sort((a, b) => a.priceCents - b.priceCents);
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('macboot-product-shipping')) {
  class ProductShipping extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.config = JSON.parse(this.querySelector('[data-shipping-config]').textContent);
      this.scope = document.getElementById(`shopify-section-${this.dataset.section}`);
      this.input = this.querySelector('[data-shipping-cep]');
      this.button = this.querySelector('[data-shipping-calculate]');
      this.status = this.querySelector('[data-shipping-status]');
      this.results = this.querySelector('[data-shipping-results]');
      this.cache = new Map();
      this.attempts = [];
      this.sequence = 0;
      const calculate = () => this.calculate();
      const enter = (event) => { if (event.key === 'Enter') { event.preventDefault(); this.calculate(); } };
      const changed = () => queueMicrotask(() => this.invalidate());
      const clicked = (event) => { if (event.target.closest('[data-qty]')) setTimeout(changed, 0); };
      this.button.addEventListener('click', calculate);
      this.input.addEventListener('keydown', enter);
      this.scope?.addEventListener('input', changed);
      this.scope?.addEventListener('change', changed);
      this.scope?.addEventListener('click', clicked);
      this.cleanup = () => {
        this.cancel();
        this.button.removeEventListener('click', calculate);
        this.input.removeEventListener('keydown', enter);
        this.scope?.removeEventListener('input', changed);
        this.scope?.removeEventListener('change', changed);
        this.scope?.removeEventListener('click', clicked);
      };
    }
    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
    selection() {
      const form = this.scope?.querySelector('[data-product-form]');
      return { variantId: form?.querySelector('[name="id"]')?.value || '', quantity: Number(form?.querySelector('[name="quantity"]')?.value || 1), postalCode: this.input.value };
    }
    key(selection = this.selection()) { return JSON.stringify(selection); }
    cancel() {
      this.sequence += 1;
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.button.disabled = false;
      this.button.textContent = this.config.calculate;
      this.removeAttribute('aria-busy');
    }
    invalidate() {
      if (this.currentKey && this.key() !== this.currentKey) {
        this.cancel(); this.currentKey = null;
        this.results.replaceChildren(); this.results.hidden = true;
        this.status.textContent = this.config.changed;
      }
    }
    message(code) { this.status.textContent = this.config[code] || this.config.unavailable; }
    renderRates(rates) {
      this.results.replaceChildren();
      this.results.hidden = !rates.length;
      this.status.textContent = rates.length ? this.config.success : this.config.empty;
      for (const rate of rates) {
        const row = document.createElement('li');
        const service = document.createElement('span'); service.className = 'mb-shipping-service';
        const name = document.createElement('span'); name.textContent = rate.name;
        const deadline = document.createElement('small');
        deadline.textContent = deliveryLabel(rate.deliveryDaysMin, rate.deliveryDaysMax);
        service.append(name, deadline);
        const price = document.createElement('strong');
        price.textContent = rate.priceCents === 0 ? this.config.free : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(rate.priceCents / 100);
        row.append(service, price); this.results.append(row);
      }
    }
    async calculate() {
      const selection = this.selection();
      this.cancel();
      this.results.replaceChildren(); this.results.hidden = true;
      const variant = this.config.variants.find((item) => String(item.id) === selection.variantId);
      if (!variant) return this.message('select_variant');
      if (!variant.available) return this.message('stock_changed');
      try { normalizePostalCode(selection.postalCode); } catch { return this.message('invalid_cep'); }
      if (!Number.isInteger(selection.quantity) || selection.quantity < 1 || selection.quantity > 30) return this.message('invalid_quantity');
      this.currentKey = this.key(selection);
      const cached = this.cache.get(this.currentKey);
      if (cached && Date.now() - cached.at < 120000) return this.renderRates(cached.rates);
      this.attempts = this.attempts.filter((time) => Date.now() - time < 60000);
      if (this.attempts.length >= 8) return this.message('rate_limited');
      this.attempts.push(Date.now());
      const sequence = this.sequence;
      this.controller = new AbortController();
      this.timeout = setTimeout(() => this.controller.abort(), 25000);
      this.button.disabled = true; this.button.textContent = this.config.loading;
      this.setAttribute('aria-busy', 'true'); this.status.textContent = this.config.loading;
      try {
        const rates = await quoteProductWithDelivery({ ...selection, handle: this.config.handle,
          endpoint: 'https://macboot-fretes.onrender.com/api/storefront/quotes', signal: this.controller.signal });
        if (sequence !== this.sequence || this.key() !== this.currentKey) return;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(this.currentKey, { rates, at: Date.now() });
        this.renderRates(rates);
      } catch (error) {
        if (sequence === this.sequence) this.message(error.code);
      } finally {
        if (sequence === this.sequence) {
          clearTimeout(this.timeout); this.button.disabled = false;
          this.button.textContent = this.config.calculate; this.removeAttribute('aria-busy');
        }
      }
    }
  }
  customElements.define('macboot-product-shipping', ProductShipping);
}
